// The reusable DQ display component. Callers pass a DQ id and nothing else:
// it fetches the question, renders the text through MathJax, renders the
// answer options, and exposes interaction hooks for picking an answer.
//
// It knows nothing about sessions, timers, follow-up questions, topics or
// phases. Those belong to the orchestrator, BmdTestPlayer.
// This component is used verbatim in:
//   - the main base-test player (mode="test")
//   - FM retry flow (mode="test", different dqId)
//   - prereq chain walker (mode="test")
//   - tutor preview modals (mode="display-only")
//   - post-topic reports showing what the student saw (mode="review")

import React from "react"
import { MathJax } from "better-react-mathjax"
import { useDQ } from "../../hooks/diagnostics/useDQ"
import { BMD_UI } from "../../constants/diagnostics/bmd.theme"
import type {
  DQAnswer,
  DQDisplayMode,
} from "../../modules/diagnostics/types/bmd.types"

export type DQDisplayProps = {
  dqId: number

  // Display mode. Defaults to "test".
  mode?: DQDisplayMode

  // Pre-select an answer (for resume in test mode, or showing the picked
  // answer in review mode).
  preselectedAnswerId?: number

  // For review / display-only, required if the backend needs to verify the
  // caller is entitled to see `answer_is_correct` (via session_question entitlement).
  sessionQuestionKey?: string

  // Fired in test mode whenever the student picks an answer. Orchestrator
  // captures this to pause the timer, surface follow-up questions, etc.
  onAnswerSelected?: (answerId: number) => void

  // Freezes the options while keeping the chosen one visible. The orchestrator
  // sets this once an answer is picked: the timer stops at that moment, so
  // leaving the options live lets the student reconsider indefinitely and then
  // switch, which also lets the "why did you choose this" reason end up
  // attached to a different answer than the one it was written about.
  locked?: boolean

  // Outer wrapper className so parents can control layout/spacing.
  className?: string
}

const renderHtml = (html?: string): { __html: string } => ({
  __html: html || "",
})

// Some answer_text rows in the DB are wrapped in <p>...</p> (from LCMS paste)
// and some are stored as bare text. The global `.question_container p` rule
// bumps wrapped content to 20px, so a bare row on the same screen renders at
// the parent's 16px and looks like a different font. Normalize by stripping
// outer <p> tags so every answer renders as bare inline content.
const stripOuterParagraph = (html?: string): string => {
  if (!html) return ""
  return html.replace(/^\s*<p[^>]*>/i, "").replace(/<\/p>\s*$/i, "")
}

// One answer option. Rendered differently based on mode.
//
// Redrawn to the PM's mockup: a bordered tile carrying a lettered circle and
// the answer, with the chosen one outlined and ticked. The radio input is gone
// - the whole tile has always been the click target, and a radio beside a tile
// that also highlights was two controls saying the same thing. Which answer
// counts as selected, and when, is unchanged.
//
// Review mode keeps its green/red fills. That is the tutor's view of a finished
// attempt, and the correct-answer colouring is the point of it.
const DQAnswerOption: React.FC<{
  answer: DQAnswer
  index: number
  selected: boolean
  mode: DQDisplayMode
  locked?: boolean
  onClick: () => void
}> = ({ answer, index, selected, mode, locked = false, onClick }) => {
  const letter = String.fromCharCode(65 + index) // A, B, C, D

  const isCorrect = Number(answer.answer_is_correct) === 1
  const showCorrectness = mode === "review"

  // Highlighting rules:
  //   review mode:  correct answer green, picked-but-wrong red, others neutral
  //   test mode:    only the selected one outlined
  //   display-only: neutral, all the same
  let bgClass = ""
  if (showCorrectness) {
    if (isCorrect) bgClass = "correct-ans"
    else if (selected && !isCorrect) bgClass = "wrong-ans"
  }

  const outlined = !showCorrectness && mode === "test" && selected
  const interactive = mode === "test" && !locked

  return (
    <div
      className={`d-flex align-items-center gap-3 ${bgClass}`}
      style={{
        cursor: interactive ? "pointer" : "default",
        padding: "14px 16px",
        borderRadius: 10,
        border: `${
          outlined
            ? BMD_UI.CONTROL_SELECTED_BORDER_WIDTH
            : BMD_UI.CONTROL_BORDER_WIDTH
        }px solid ${
          outlined ? BMD_UI.OPTION_SELECTED_BORDER : BMD_UI.CONTROL_BORDER
        }`,
        // Review mode's fills come from the class, so no inline colour there.
        backgroundColor: showCorrectness
          ? undefined
          : outlined
          ? BMD_UI.OPTION_SELECTED_BG
          : BMD_UI.CARD_BG,
        height: "100%",
      }}
      onClick={() => {
        if (interactive) onClick()
      }}
      role={interactive ? "button" : undefined}
      aria-pressed={interactive ? selected : undefined}
    >
      <span
        className="d-inline-flex align-items-center justify-content-center fw-bold flex-shrink-0"
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          fontSize: 12,
          backgroundColor: outlined
            ? BMD_UI.OPTION_SELECTED_BORDER
            : BMD_UI.LETTER_BG,
          color: outlined ? "#fff" : BMD_UI.LETTER_TEXT,
          border: outlined ? "none" : `1px solid ${BMD_UI.LETTER_BORDER}`,
        }}
      >
        {letter}
      </span>

      <div
        className="flex-grow-1 font-size-16 bmd-answer-body"
        style={{ color: BMD_UI.TITLE, minWidth: 0, fontWeight: 700 }}
        dangerouslySetInnerHTML={renderHtml(stripOuterParagraph(answer.answer_text))}
      />

      {outlined ? (
        <i
          className="fas fa-check flex-shrink-0"
          style={{ color: BMD_UI.OPTION_SELECTED_BORDER }}
          aria-hidden="true"
        ></i>
      ) : null}

      {showCorrectness && isCorrect ? (
        <span className="badge bg-success text-white flex-shrink-0">
          Correct
        </span>
      ) : null}

      {showCorrectness && answer.misconception_type ? (
        <span className="badge bg-warning text-dark flex-shrink-0 ms-2">
          {answer.misconception_type.replace(/_/g, " ")}
        </span>
      ) : null}
    </div>
  )
}

export const DQDisplay: React.FC<DQDisplayProps> = ({
  dqId,
  mode = "test",
  preselectedAnswerId,
  sessionQuestionKey,
  onAnswerSelected,
  locked = false,
  className,
}) => {
  const { dq, loading, error } = useDQ(dqId, mode, sessionQuestionKey)
  const [selectedId, setSelectedId] = React.useState<number | null>(
    preselectedAnswerId ?? null,
  )

  React.useEffect(() => {
    if (preselectedAnswerId != null) {
      setSelectedId(preselectedAnswerId)
    }
  }, [preselectedAnswerId])

  const handleSelect = (answerId: number) => {
    if (mode !== "test" || locked) return
    setSelectedId(answerId)
    onAnswerSelected?.(answerId)
  }

  if (loading) {
    return (
      <div className={className}>
        <p className="text-muted mb-0">Loading question…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={className}>
        <p className="text-danger mb-0">{error}</p>
      </div>
    )
  }

  if (!dq) {
    return null
  }

  return (
    <div className={className}>
      {/* Keyed by dq.diagnostic_id so MathJax fully unmounts between questions.
          `dynamic` intentionally omitted: keying per-question guarantees a
          fresh mount/typeset, and `dynamic` would otherwise re-trigger
          typesetting on parent re-renders and can fire async against a
          torn-down DOM → "Cannot read properties of null (reading 'nextSibling')". */}
      <MathJax key={dq.diagnostic_id} inline>
        {/* `question_container` is borrowed for its TYPOGRAPHY only: the
            `.question_container p, div` rule normalises DB-stored HTML, which
            arrives sometimes wrapped in <p> and sometimes bare, and without it
            the two render at different sizes on the same screen.

            The class ALSO carries a bordered, fixed-height panel meant for the
            other student UI - min-height calc(50dvh - 140px), a 2.5px navy
            border and a box-shadow. That is where the big empty box around the
            question came from: not from the question's own HTML, and not from
            anything the diagnostics code draws. Those properties are reset
            here rather than by dropping the class, so the typography rule
            still applies. None of them are !important, so inline wins.

            If the panel styling is ever wanted here, take it off this class
            and give the other UI its own. */}
        <div
          className="fw-bold question_container"
          style={{
            color: BMD_UI.TITLE,
            fontSize: 21,
            lineHeight: 1.45,
            padding: "12px 8px 0 16px",
            marginBottom: 20,
            border: "none",
            borderRadius: 0,
            boxShadow: "none",
            backgroundColor: "transparent",
            minHeight: 0,
            maxHeight: "none",
            overflowY: "visible",
          }}
          dangerouslySetInnerHTML={renderHtml(dq.diagnostic_text)}
        />

        {/* Two across on anything wider than a phone, as drawn. A four-option
            question then reads as a 2x2 block the eye takes in at once, rather
            than a column that pushes Submit below the fold while a 60-second
            clock is running. */}
        <ul className="list-unstyled mb-0 row g-3">
          {dq.answers.map((answer, index) => (
            <li key={answer.answer_id} className="col-12 col-md-6">
              <DQAnswerOption
                answer={answer}
                index={index}
                selected={selectedId === answer.answer_id}
                mode={mode}
                locked={locked}
                onClick={() => handleSelect(answer.answer_id)}
              />
            </li>
          ))}
        </ul>
      </MathJax>
    </div>
  )
}

export default DQDisplay
